"use client";
/*
  HOMEPAGE LOOK. Keep this page's title, hero and sections as they are. On
  4 Nov 2026 the owner reverted the SEO additions that changed how it looks: an
  "AI video clipping" line in the H1, a "What is Klap?" block and a block of tool
  links. Facts for crawlers and LLMs go in the FAQ answers, the JSON-LD, the meta
  tags and public/llms.txt.
*/
import SparklesIcon from "../../components/SparklesIcon";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useRef, useState } from "react";
import { useEventListener } from "usehooks-ts";
import Header from "../../components/Header";
import Hero from "../../components/Hero";
import Logo from "../../components/Logo";
import Typography from "../../components/Typography";

const TestimonialsSection = dynamic(
  () => import("../../components/TestimonialsSection"),
  { ssr: false },
);
const FAQ = dynamic(() => import("../../components/FAQ"));
const Footer = dynamic(() => import("../../components/Footer"));
const BenefitBlock = dynamic(() => import("../../components/blocks/BenefitBlock"));
const HowToBlock = dynamic(() => import("../../components/blocks/HowToBlock"));
const FeaturesSection = dynamic(
  () => import("../../components/blocks/FeaturesBlock"),
);
import { SECTION_LG_MAX_W, SECTION_MD_MAX_W } from "../../const/twConst";
import {
  MediaSelector,
  WEB_ASSET_DEMO_MEDIA,
  WEB_ASSET_SHORT_URLS,
} from "../../helpers/media-landing";
import EmptyCaptionsTrack from "../../components/EmptyCaptionsTrack";
import { useLocale } from "../../context/i18n";
import { home as enHome, type HomeCopy } from "../../dictionaries/en/home";
import { useInViewVideo } from "../../hooks/useInViewVideo";
import { formatNumber } from "../../utils/format-number";

function InViewVideo(
  props: React.VideoHTMLAttributes<HTMLVideoElement>
) {
  const ref = useInViewVideo<HTMLVideoElement>();
  return <video ref={ref} {...props} />;
}

export interface HomeProps {
  metrics: {
    clips: number;
    users: number;
  };
  // This page's text in the visitor's language.
  copy?: HomeCopy;
}

export default function Home({ copy = enHome, ...props }: HomeProps) {
  const lang = useLocale();
  const mediaSelector = new MediaSelector(WEB_ASSET_SHORT_URLS, "landing");
  const [isIntersecting, setIsIntersecting] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEventListener("scroll", () => {
    const headerHeight = headerRef.current?.offsetHeight ?? 0;
    const sectionTop = sectionRef.current?.offsetTop ?? 0;
    const sectionHeight = sectionRef.current?.offsetHeight ?? 0;
    const sectionBottom = sectionTop + sectionHeight;
    const scrollY = window.scrollY;
    if (
      scrollY >= sectionTop - headerHeight &&
      scrollY <= sectionBottom - headerHeight
    )
      return setIsIntersecting(true);
    setIsIntersecting(false);
  });

  return (
    <>
      <main className="min-h-screen mx-auto text-slate-950">
        <Header
          ref={headerRef}
          className={`w-full ${
            isIntersecting ? "bg-slate-950 dark" : ""
          }  mx-auto `}
        />

        <Hero
          className="w-full mt-8 md:mt-16 max-w-[1500px] px-3 md:px-12"
          usersCount={props.metrics.users}
          copy={copy.hero}
        />

        <section
          ref={sectionRef}
          className="bg-slate-950 text-white my-28 py-28"
        >
          <div className="max-w-[1500px] px-3 md:px-12 mx-auto flex flex-col items-center text-center">
            <div className="mx-auto flex items-center gap-2 justify-center bg-slate-800 rounded-full backdrop-blur-sm px-4 py-1 text-slate-300 mb-6">
              <SparklesIcon variant="mini" className="w-3 h-3" />
              <Typography variant="subtitle3">{copy.reframe.badge}</Typography>
            </div>
            <Typography variant="h2" className="">
              {copy.reframe.title}
            </Typography>
            <Typography
              variant="body1"
              className="text-slate-450 mt-8 max-w-3xl mx-auto"
            >
              {copy.reframe.body}
            </Typography>
            <InViewVideo
              playsInline
              src={WEB_ASSET_DEMO_MEDIA.beforeAfter.src}
              loop
              muted
              preload="metadata"
              className="rounded-2xl w-full mt-8 aspect-video bg-slate-800"
            >
              <EmptyCaptionsTrack />
            </InViewVideo>
          </div>
        </section>

        <section className="max-w-[1500px] px-3 md:px-12 mx-auto text-center mb-32 md:mb-64 mt-28">
          <Typography variant="h2" className="block max-w-3xl mx-auto">
            {copy.stats.title
              .replace("{clips}", formatNumber(props.metrics.clips, lang))
              .replace("{users}", formatNumber(props.metrics.users, lang))}
          </Typography>
          <Typography
            variant="body1"
            className="mt-8 block max-w-3xl mx-auto text-slate-500"
          >
            {copy.stats.body}
          </Typography>
          <div className="flex gap-2 mt-12 relative">
            {Array.from({ length: 7 }).map((_, index) => (
              <div
                key={index}
                className="rounded-xl isolate md:rounded-2xl overflow-hidden w-full aspect-[9/16]"
              >
                <InViewVideo
                  controls={false}
                  disableRemotePlayback
                  playsInline
                  muted
                  width="100%"
                  height="100%"
                  loop
                  preload="metadata"
                  src={mediaSelector.getNextUrl()}
                />
              </div>
            ))}
            <div className="flex absolute left-0 inset-y-0">
              <div className="w-8 bg-white" />
              <div className="w-32 bg-gradient-to-l from-transparent to-white" />
            </div>
            <div className="flex absolute right-0 inset-y-0">
              <div className="w-32 bg-gradient-to-r from-transparent to-white" />
              <div className="w-8 bg-white" />
            </div>
          </div>
          <div className="flex justify-center items-center gap-2 mt-8">
            <Typography className="text-slate-500" variant="body2">
              {copy.stats.madeWith}
            </Typography>
            <Logo className="!w-18" />
          </div>
        </section>

        {copy.benefits.map((benefit) => (
          <BenefitBlock
            className="text-start max-w-[1500px] px-3 md:px-12"
            key={benefit.title}
            data={benefit}
          />
        ))}

        <HowToBlock data={copy.howTo} className="max-w-[1500px] md:px-12 px-3" />
        <FeaturesSection
          data={copy.features}
          className="my-32 md:!my-64  max-w-[1500px] px-3 md:px-12 mx-auto"
        />
        <section className="mx-auto w-full flex items-center flex-col justify-center text-center max-w-6xl px-3 md:px-12">
          <Image
            src="https://assets.klap.app/web-assets/klap-long-video.png"
            className="max-w-xs -mb-8 h-auto"
            alt={copy.grow.longVideoAlt}
            width={320}
            height={197}
            sizes="(max-width: 768px) 80vw, 320px"
          />
          <div className="relative">
            <Typography
              className="mt-4 max-w-3xl mx-auto z-10 relative"
              variant="h2"
            >
              {copy.grow.title}
            </Typography>
            <Typography
              variant="body"
              className="text-slate-600 max-w-3xl mx-auto mt-8 z-10 relative"
            >
              {copy.grow.body}
            </Typography>
            <Image
              src="https://assets.klap.app/web-assets/klap-ai-arrow.png"
              className="absolute left-full top-1/2 -translate-y-1/2 w-28 -ml-8 mt-8 md:block hidden h-auto"
              alt=""
              width={112}
              height={112}
              sizes="112px"
            />
          </div>
          <Image
            src="https://assets.klap.app/web-assets/klap-shorts.png"
            className="max-w-full md:max-w-xl mt-8 -ml-4 h-auto"
            alt={copy.grow.shortsAlt}
            width={576}
            height={326}
            sizes="(max-width: 768px) 100vw, 576px"
          />
        </section>
        <TestimonialsSection
          className={`${SECTION_LG_MAX_W} my-32 md:my-64`}
          title={copy.testimonialsTitle}
        />
        <FAQ className={`${SECTION_MD_MAX_W} my-64`} faq={copy.faq} jsonLd />
        <Footer />
      </main>
    </>
  );
}
